import { useCallback, useEffect, useState } from "react"
import Select from "../Select"
import styles from "./UnitSelector.module.css"
import TextInput from "../TextInput"

const options = [
    { id: 0, name: 'rem', value: 'rem' },
    { id: 1, name: 'px', value: 'px' },
    { id: 2, name: 'em', value: 'em' },
    { id: 3, name: '%', value: '%' },
    { id: 4, name: 'vh', value: 'vh' },
    { id: 5, name: 'vw', value: 'vw' },
    { id: 6, name: 'dvh', value: 'dvh' },
    { id: 7, name: 'min-content', value: 'min-content' },
    { id: 8, name: 'max-content', value: 'max-content' },
    { id: 9, name: 'fit-content', value: 'fit-content' },
    { id: 10, name: 'auto', value: 'auto' },
    { id: 11, name: 'deg', value: 'deg' },
]

const parseValue = (val) => {
    if (["min-content", "max-content", "fit-content", "auto"].includes(val)) {
        return [null, val];
    }
    if (!val || val.length === 0) return ["", ""];
    const match = val.match(/^(-?\d*\.?\d+)([a-zA-Z%]+)$/);
    if (!match) return ["", ""];
    return [parseFloat(match[1]), match[2]];
}

const SPECIAL_UNITS = ["min-content", "max-content", "fit-content", "auto"];

const UnitSelector = ({ value, onChange, label, defaultUnit, disableUnit }) => {
    const [number, setNumber] = useState(parseValue(value)[0]);
    const [unit, setUnit] = useState(parseValue(value)[1] || defaultUnit || options[0].value);
    const [inputFocused, setInputFocused] = useState(false);

    useEffect(() => {
        const [n, u] = parseValue(value);
        setNumber(n);
        // Preserve current unit when clearing input (u === '')
        if (u) setUnit(u);
    }, [value]);

    const applyNumericChange = useCallback((nextValue) => {
        setNumber(nextValue);
        let targetUnit = unit;
        if (!targetUnit || SPECIAL_UNITS.includes(targetUnit)) {
            targetUnit = defaultUnit || options[0].value;
            setUnit(targetUnit);
        }
        if (targetUnit) {
            onChange(`${nextValue}${targetUnit}`);
        } else {
            onChange(nextValue);
        }
    }, [unit, defaultUnit, onChange]);

    useEffect(() => {
        if (!inputFocused) {
            return;
        }
        const handleKeyDown = (event) => {
            if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
                return;
            }
            event.preventDefault();
            const delta = event.key === 'ArrowUp' ? 1 : -1;
            const currentValue = typeof number === 'number' && !Number.isNaN(number) ? number : 0;
            const nextValue = currentValue + delta;
            applyNumericChange(nextValue);
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [inputFocused, number, applyNumericChange]);



    return (
        <div className={styles.unitSelector}>
            {/* {label && <div className={typo.caption}>{label}</div>} */}
            <div className={styles.container}>
                <div style={{ position: 'relative', width: '100%' }}>
                    <TextInput appearance="modern"
                        slideToControl
                        type="number"
                        numberType="float"
                        decimals={3}
                        placeholder={label}
                        value={isNaN(parseFloat(number)) ? '' : `${parseFloat(number)}`}
                        style={{
                            border: 'none',
                            fontSize: '0.75rem',
                            fontWeight: 400,
                            padding: '.5rem',
                            borderRight: '1px solid rgba(var(--text-color-rgb), 12%)',
                            borderRadius: '6px 0 0 6px',
                            backgroundColor: 'var(--background-color)',
                            minHeight: '36px',
                            height: '100%',
                        }}
                        onKeyUp={(v, rawV) => {
                            if (rawV === 'min' || rawV === 'max' || rawV === 'fit') {
                                setNumber(null);
                                setUnit(rawV + '-content');
                                onChange(rawV);
                                return;
                            }
                            if (options.map(o => o.value).some((u => rawV.endsWith(u)))) {
                                let _value = rawV
                                // cerco la unit inclusa nel rawV
                                let u = options.find(o => rawV.includes(o.value));
                                // rimuovo la unit dal valore
                                _value = rawV.replace(u.value, '');

                                if (rawV === '%') {
                                    _value = '100';
                                }

                                setNumber(_value);
                                setUnit(u.value)
                                onChange(_value + u.value)
                                return
                            }
                            if (rawV === '') {
                                setNumber('');
                                onChange(null);
                                return;
                            }
                            const parsed = parseFloat(v);
                            if (!isNaN(parsed)) {
                                applyNumericChange(parsed);
                            }
                        }}
                        onFocus={(state) => {
                            setInputFocused(state);
                        }}
                    />
                    {number !== null && number !== '' &&
                        <div className={styles.label}>{label}</div>
                    }
                </div>
                <Select appearance="modern"
                    disabled={disableUnit}
                    options={options}
                    selected={options.find(o => o.value === unit) ?? options[0]}
                    placeholder=""
                    style={{ minHeight: '36px', border: 'none', borderRadius: '0 6px 6px 0', padding: '.5rem', fontSize: '.75rem' }}
                    hideArrow={true}
                    onSelect={(option) => {
                        setUnit(option.value);
                        if (SPECIAL_UNITS.includes(option.value)) {
                            onChange(option.value);
                            return;
                        }
                        if (number === '' || number === null || Number.isNaN(number)) {
                            onChange(null);
                            return;
                        }
                        onChange(`${number}${option.value}`);
                    }}
                />
            </div>
        </div>
    );
}

export default UnitSelector